/**
 * GDPR Consent Configuration
 * 
 * This file defines consent categories, versions, and plain-language descriptions.
 * Increment CONFIG_VERSION when changing categories or purposes to trigger re-consent.
 */

export const CONFIG_VERSION = '1.0';

// Consent expiry in days (GDPR recommends max 12 months)
export const CONSENT_EXPIRY_DAYS = 365;

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';

export interface CategoryConfig {
  id: ConsentCategory;
  name: string;
  description: string;
  purposes: string[];
  dataRecipients: string[];
  required: boolean; // If true, cannot be disabled
  defaultEnabled: boolean;
}

export const CONSENT_CATEGORIES: CategoryConfig[] = [
  {
    id: 'necessary',
    name: 'Necessary',
    description: 'These cookies are essential for the website to function properly. They enable core features like user authentication, language preferences, and security. Without these cookies, the website cannot operate correctly.',
    purposes: [
      'User authentication and session management',
      'Remembering your language preference',
      'Security and fraud prevention',
      'Load balancing and server optimization',
    ],
    dataRecipients: ['SimplaBooka (first-party only)'],
    required: true,
    defaultEnabled: true,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'These cookies help us understand how visitors interact with our website. They collect information about page views, time spent on pages, and navigation patterns. This data helps us improve our service.',
    purposes: [
      'Measuring website traffic and usage patterns',
      'Understanding which features are most popular',
      'Identifying technical issues and errors',
      'Improving website performance and user experience',
    ],
    dataRecipients: ['Google Analytics (Google LLC, USA)'],
    required: false,
    defaultEnabled: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements. They help us measure the effectiveness of our advertising campaigns and may be used for remarketing purposes.',
    purposes: [
      'Tracking conversions from advertisements',
      'Measuring advertising campaign effectiveness',
      'Building audience segments for remarketing',
      'Displaying personalized advertisements on other platforms',
    ],
    dataRecipients: ['Meta Platforms (Facebook/Instagram, USA)', 'Google Ads (Google LLC, USA)'],
    required: false,
    defaultEnabled: false,
  },
];

export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const DEFAULT_CONSENT_STATE: ConsentState = {
  necessary: true, // Always enabled
  analytics: false,
  marketing: false,
};

/**
 * Get category config by ID
 */
export function getCategoryConfig(categoryId: ConsentCategory): CategoryConfig | undefined {
  return CONSENT_CATEGORIES.find(c => c.id === categoryId);
}

/**
 * Check if all non-required categories are accepted
 */
export function isFullConsent(state: ConsentState): boolean {
  return state.analytics && state.marketing;
}

/**
 * Check if only necessary cookies are accepted
 */
export function isMinimalConsent(state: ConsentState): boolean {
  return state.necessary && !state.analytics && !state.marketing;
}

